import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Stack,
  FormHelperText,
  InputLabel,
  FormControl,
  Select,
  Snackbar,
  Alert,
  Typography,
  Chip,
  Box,
} from "@mui/material";
import { collection, addDoc, Timestamp, query, where, getDocs } from "firebase/firestore";
import { db } from "../../firebase";

import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  EXPENSE_CATEGORIES,
  DEDUCTIBILITY,
} from "../../constants/expenseCategories";

// Map deductibility to display info
const getDeductibilityInfo = (deductibility) => {
  switch (deductibility) {
    case DEDUCTIBILITY.FULL:
      return { label: "100% Deductible", color: "success" };
    case DEDUCTIBILITY.PARTIAL:
      return { label: "Partially Deductible", color: "warning" };
    case DEDUCTIBILITY.CONDITIONAL:
      return { label: "Conditionally Deductible", color: "info" };
    case DEDUCTIBILITY.NONE:
      return { label: "Not Deductible", color: "error" };
    default:
      return { label: "Check with accountant", color: "default" };
  }
};

export default function AddManualExpenseDialog({
  open,
  onClose,
  user,
  onAdded,
}) {
  // Resettable initial state
  const initialState = {
    amount: "",
    date: new Date().toISOString().slice(0, 10),
    category: "",
    notes: "",
    vatIncluded: false,
  };

  const [amount, setAmount] = useState(initialState.amount);
  const [date, setDate] = useState(initialState.date);
  const [category, setCategory] = useState(initialState.category);
  const [notes, setNotes] = useState(initialState.notes);
  const [vatIncluded, setVatIncluded] = useState(initialState.vatIncluded);
  const [loading, setLoading] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  // Project selection state
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        if (!user) return;
        const snap = await getDocs(
          query(collection(db, "projects"), where("uid", "==", user.uid))
        );
        const loadedProjects = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProjects(loadedProjects);
      } catch (error) {
        console.error("Failed to load projects", error);
      }
    };

    if (open) {
      fetchProjects();
    }
  }, [user, open]);
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Ref for Amount field
  const amountRef = useRef();

  // Focus on Amount when dialog opens or after clearing
  useEffect(() => {
    if (open && amountRef.current) {
      setTimeout(() => {
        amountRef.current.focus();
      }, 100); // small delay to ensure dialog is rendered
    }
  }, [open]);

  // Clear all fields to initial state and focus Amount
  const clearFields = () => {
    setAmount(initialState.amount);
    setDate(new Date().toISOString().slice(0, 10));
    setCategory(initialState.category);
    setNotes(initialState.notes);
    setVatIncluded(initialState.vatIncluded);
    setSelectedProjectId("");
    setTimeout(() => {
      if (amountRef.current) amountRef.current.focus();
    }, 100);
  };

  const handleSave = async () => {
    if (!amount || !category) return;
    setLoading(true);
    try {
      const dt = new Date(date);
      await addDoc(collection(db, "receipts"), {
        uid: user.uid,
        amount: parseFloat(amount),
        receiptDate: date,
        receiptMonth: dt.getMonth(),
        receiptYear: dt.getFullYear(),
        category,
        notes,
        vat: vatIncluded
          ? Math.round(parseFloat(amount) * 0.23 * 100) / 100
          : 0,
        isManual: true,
        createdAt: Timestamp.now(),
        fileName: notes ? notes : "Manual Entry",
        uploadedAt: Timestamp.now(),
        projectId: selectedProjectId || null,
      });
      if (onAdded) onAdded();
      setSnackbar({
        open: true,
        message: "✅ Manual expense added.",
        severity: "success",
      });
      clearFields();
      // Dismiss modal after a successful save!
      setTimeout(onClose, 300); // allow a slight delay for UX, can be 0 if you want instant
    } catch (e) {
      alert("Failed to save manual expense.");
    }
    setLoading(false);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Add Manual Business Expense</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField
              label="Amount"
              value={amount}
              inputRef={amountRef}
              onChange={(e) =>
                setAmount(e.target.value.replace(/[^0-9.]/g, ""))
              }
              type="number"
              required
              fullWidth
              InputProps={{ startAdornment: <span>€</span> }}
            />

            {/* DatePicker: Clicking anywhere opens calendar */}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date"
                open={datePickerOpen}
                onOpen={() => setDatePickerOpen(true)}
                onClose={() => setDatePickerOpen(false)}
                value={date ? new Date(date) : null}
                onChange={(newDate) => {
                  setDatePickerOpen(false);
                  if (newDate instanceof Date && !isNaN(newDate)) {
                    setDate(newDate.toISOString().slice(0, 10)); // yyyy-mm-dd
                  }
                }}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    InputLabelProps: { shrink: true },
                    onClick: () => setDatePickerOpen(true),
                    sx: { cursor: "pointer" },
                  },
                }}
              />
            </LocalizationProvider>

            <FormControl required fullWidth>
              <InputLabel id="category-select-label">Category</InputLabel>
              <Select
                labelId="category-select-label"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                label="Category"
              >
                {EXPENSE_CATEGORIES.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Select the expense category</FormHelperText>
            </FormControl>

            {/* Tax Guidance for Selected Category */}
            {category &&
              (() => {
                const selectedCat = EXPENSE_CATEGORIES.find(
                  (c) => c.value === category
                );
                if (!selectedCat) return null;
                const deductInfo = getDeductibilityInfo(
                  selectedCat.deductibility
                );
                return (
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: "grey.50",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "grey.200",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      mb={0.5}
                    >
                      <Chip
                        label={deductInfo.label}
                        color={deductInfo.color}
                        size="small"
                      />
                      {selectedCat.vatReclaimable && (
                        <Chip
                          label="VAT Reclaimable"
                          color="primary"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {selectedCat.guidance}
                    </Typography>
                  </Box>
                );
              })()}

            <TextField
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={2}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel id="project-select-label">
                Assign to Project (optional)
              </InputLabel>
              <Select
                labelId="project-select-label"
                label="Assign to Project (optional)"
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {projects.map((proj) => (
                  <MenuItem key={proj.id} value={proj.id}>
                    {proj.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Choose a project to link this expense.
              </FormHelperText>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={vatIncluded}
                  onChange={(e) => setVatIncluded(e.target.checked)}
                />
              }
              label="Amount includes VAT (23%)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!amount || !category || loading}
          >
            {loading ? "Saving..." : "Save Expense"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
